
import { Component, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { PERMISSION } from '@/permissions';
import Toast from '@/utils/toast';
import CalendarWidget from '@/components/dashboard/CalendarWidget.vue';
import DashboardInquiries from '@/components/dashboard/DashboardInquiries.vue';
import DashboardQuotes from '@/components/dashboard/DashboardQuotes.vue';
import FullPageSpinner from '@/components/shared/suites/FullPageSpinner.vue';
import OrderModal from '@/components/order-management/OrderModal.vue';
import OrdersSummary from '@/components/dashboard/OrdersSummary.vue';
import PendingOrders from '@/components/dashboard/PendingOrders.vue';
import SuitesHeader from '@/components/shared/suites/SuitesHeader.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';
import TextField from '@/components/shared/TextField.vue';

const namespace: string = 'dashboard';

@Component({
	components: {
		CalendarWidget,
		DashboardInquiries,
		DashboardQuotes,
		FullPageSpinner,
		OrderModal,
		OrdersSummary,
		PendingOrders,
		SuitesHeader,
		TextButton,
		TextField
	}
})
export default class Dashboard extends Vue {
	@Action('fetchDashboardData', { namespace }) private fetchDashboardData!: () => Promise<{ areThereNewOrders: boolean, fetchDate: string }>;
	@Action('resetDashboardFilters', { namespace }) private resetDashboardFilters!: () => Promise<void>;
	@Action('fetchOrderById', { namespace: 'orders' }) private fetchOrderById!: (payload: { orderId: number }) => Promise<Order>;
	@Action('setDesktopNavExpanded', { namespace: 'ui' }) private setDesktopNavExpanded!: (isExpanded: boolean) => void;
	@Getter('getFilters', { namespace }) private filters!: DashboardFilters;
	@Getter('getRestaurant', { namespace: 'auth' }) private restaurant!: Restaurant;
	@Getter('hasPermission', { namespace: 'auth' }) private hasPermission!: (permission: PERMISSION) => boolean;
	@Getter('isOrderManagementEnabled', { namespace: 'auth' }) private isOrderManagementEnabled!: boolean;
	@Getter('areNotificationSoundsAllowed', { namespace: 'auth' }) private areNotificationSoundsAllowed!: boolean;
	@Getter('areCateringEventsEnabled', { namespace: 'auth' }) private areCateringEventsEnabled!: boolean;
	@Getter('hasExtraPaymentMethodsConfigured', { namespace: 'auth' }) private hasExtraPaymentMethodsConfigured!: boolean;

	private intervalId: number | undefined;
	private areThereNewOrders: boolean = false;
	private isInitialLoading: boolean = true;
	private fetchingOrder: boolean = false;
	private orderModalOpen: boolean = false;
	private selectedOrder: Order | null = null;
	private searchInput: string = '';
	private PERMISSION = PERMISSION;

	private audio: HTMLAudioElement = new Audio(require('@/assets/audio/notification.ogg'));

	/**
	 * Determine if create order button should be shown based on user role and restaurant configuration
	 * Requires proper contact field configuration and payment methods for catering locations
	 *
	 * @return {boolean}
	 */
	private get showCreateOrderButton(): boolean {
		// Check that all contact fields are required and not hidden
		const contactFieldsConfig = this.restaurant.menu_manager_config?.contact_fields_config as Record<string, ContactFieldSettings> | undefined;
		const contactFieldsRequired = ['name', 'email', 'phone_number'].every(field => !!(contactFieldsConfig?.[field]?.required && !contactFieldsConfig?.[field]?.hide));

		return this.hasPermission(PERMISSION.ADMIN_ONLY) || this.restaurant.catering && contactFieldsRequired && this.hasExtraPaymentMethodsConfigured && this.hasPermission(PERMISSION.ORDER_CREATE_CATERING);
	}

	/**
	 * Set default filters and date range (month), then fetch initial data
	 *
	 * @return {Promise<void>}
	 */
	private async created(): Promise<void> {
		this.setDesktopNavExpanded(false);

		try {
			this.isInitialLoading = true;
			await this.resetDashboardFilters();
		}
		catch (errorMessage) {
			Toast.error(this.$t('orders.dashboard.error_fetching_data', { errorMessage }));
		}
		finally {
			this.isInitialLoading = false;
		}

		this.setFetchInterval();
	}

	/**
	 * Cleanup any running intervals when component is destroyed
	 *
	 * @return {void}
	 */
	private beforeDestroy(): void {
		if (this.intervalId) {
			clearInterval(this.intervalId);
		}
	}

	/**
	 * Set interval to fetch dashboard data every 10 seconds
	 * Can be used for real-time data updates
	 *
	 * @return {void}
	 */
	private async setFetchInterval(): Promise<void> {
		this.intervalId = window.setInterval(async () => {
			try {
				// Reset before fetching so the watcher triggers even if areThereNewOrders was already true
				this.areThereNewOrders = false;
				const { areThereNewOrders, fetchDate } = await this.fetchDashboardData();
				this.areThereNewOrders = areThereNewOrders;

				if (areThereNewOrders && this.areNotificationSoundsAllowed) {
					this.audio.play().catch(() => {});
				}
				this.$store.commit('dashboard/SET_FILTERS', { ...this.filters, lastOrder: fetchDate });
			}
			catch (errorMessage) {
				Toast.error(this.$t('orders.dashboard.error_fetching_data', { errorMessage }));
			}
		}, 10000);
	}

	/**
	 * Open order details modal by fetching order data and setting modal state
	 * Handles errors gracefully if order cannot be fetched
	 *
	 * @param {number} orderId
	 * @return {Promise<void>}
	 */
	private async openOrderModal(orderId: number): Promise<void> {
		try {
			this.fetchingOrder = true;
			this.selectedOrder = await this.fetchOrderById({ orderId });

			this.fetchingOrder = false;
			this.orderModalOpen = true;
		}
		catch (errorMessage) {
			Toast.error(this.$t('orders.dashboard.error_fetching_order', { errorMessage }));

			this.fetchingOrder = false;
		}
	}

	/**
	 * Navigate to order management page with search query
	 * Used when user performs a search from dashboard
	 *
	 * @return {void}
	 */
	private searchOrders(): void {
		this.$router.push({ path: '/order-management', query: { search: this.searchInput } }).catch(() => {});
	}
}
